<template>
  <div>
    <div class="float-right mb-5" v-if="!granularity">
      <v-select v-model="granularityInternal" :items="granularityItems" :label="$t('components.userData.DataSeriesChart.granularity')"></v-select>
    </div>
    <v-chart :option="options" :init-options="{renderer: 'canvas'}" autoresize style="height: 300px; width: 100%;"/>
  </div>
</template>

<script>
import ECharts from 'vue-echarts'
import 'echarts/lib/chart/line'
import 'echarts/lib/component/tooltip'
import 'echarts/lib/component/singleAxis'
import 'echarts/lib/component/legend'
import 'echarts/lib/component/legendScroll'
import 'echarts/lib/component/title'
import 'echarts/lib/component/toolbox'

import 'echarts/lib/component/grid'
import 'zrender/lib/canvas/canvas'
import numbro from 'numbro'
// import moment from 'moment'

export default {
  data () {
    return {
      granularityInternal: 'Monthly'
    }
  },
  computed: {
    granularityItems () {
      return [
        { text: this.$t('components.userData.DataSeriesChart.monthly'), value: 'Monthly' },
        { text: this.$t('components.userData.DataSeriesChart.quarterly'), value: 'Quarterly' },
        { text: this.$t('components.userData.DataSeriesChart.weekly'), value: 'Weekly' }
      ]
    },
    months () {
      return [
        this.$t('components.userData.DataSeriesChart.jan'),
        this.$t('components.userData.DataSeriesChart.feb'),
        this.$t('components.userData.DataSeriesChart.mar'),
        this.$t('components.userData.DataSeriesChart.apr'),
        this.$t('components.userData.DataSeriesChart.may'),
        this.$t('components.userData.DataSeriesChart.jun'),
        this.$t('components.userData.DataSeriesChart.jul'),
        this.$t('components.userData.DataSeriesChart.aug'),
        this.$t('components.userData.DataSeriesChart.sep'),
        this.$t('components.userData.DataSeriesChart.oct'),
        this.$t('components.userData.DataSeriesChart.nov'),
        this.$t('components.userData.DataSeriesChart.dec')
      ]
    },
    quarters () {
      return [
        this.$t('components.userData.DataSeriesChart.q1'),
        this.$t('components.userData.DataSeriesChart.q2'),
        this.$t('components.userData.DataSeriesChart.q3'),
        this.$t('components.userData.DataSeriesChart.q4')
      ]
    },
    unit () {
      return this.data.unit
    },
    xCategorySet () {
      const weeks = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52]
      if (this.singleSeries) {
        let years = Object.keys(this.colors)
        let arr = []
        if (this.granularityInternal === 'Monthly') {
          for (const year of years) {
            for (const month of Array.from({ length: 12 }, (_, i) => i + 1)) {
              arr.push(`${this.months[month - 1]} '${year.slice(-2)}`)
            }
          }
          return arr
        } else if (this.granularityInternal === 'Weekly') {
          for (const year of years) {
            for (let week = 1; week <= 52; week++) {
              const date = this.getDateOfISOWeek(week, parseInt(year))
              const dd = String(date.getDate()).padStart(1, '0')
              const mm = Number(String(date.getMonth() + 1).padStart(2, '0'))
              const yy = String(year).slice(-2)
              arr.push(`${this.months[mm - 1]} ${dd} '${yy}`)
            }
          }
          return arr
        } else if (this.granularityInternal === 'Quarterly') {
          for (const year of years) {
            for (const quarter of Array.from({ length: 4 }, (_, i) => i + 1)) {
              arr.push(`${this.quarters[quarter - 1]} '${year.slice(-2)}`)
            }
          }
          return arr
        } else {
          return null
        }
      } else {
        if (this.granularityInternal === 'Monthly') {
          return this.months
        } else if (this.granularityInternal === 'Weekly') {
          return weeks
        } else if (this.granularityInternal === 'Quarterly') {
          return this.quarters
        } else {
          return null
        }
      }
    },
    sortedSeries () {
      if (this.granularityInternal === 'Weekly') {
        return this.sortedSeriesByWeek
      } else if (this.granularityInternal === 'Quarterly') {
        return this.sortedSeriesByQuarter
      } else if (this.granularityInternal === 'Monthly') {
        return this.sortedSeriesByMonth
      } else {
        return null
      }
    },
    sortedSeriesByWeek () {
      return this.aggregateYearSeries('Weekly')
    },
    sortedSeriesByQuarter () {
      return this.aggregateYearSeries('Quarterly')
    },
    sortedSeriesByMonth () {
      return this.aggregateYearSeries('Monthly')
    },
    displayTitle () {
      return this.title || this.$t('components.userData.DataSeriesChart.missingTitle')
    },
    options () {
      return {
        title: {
          text: this.displayTitle
        },
        xAxis: {
          type: 'category',
          data: this.xCategorySet
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: this.formatValue
          },
          name: this.unit,
          nameLocation: 'middle',
          nameGap: 50
        },
        series: this.sortedSeries,
        tooltip: {
          trigger: 'axis',
          formatter: this.formatTooltip
        },
        legend: {
          show: !this.singleSeries,
          data: this.sortedSeries.map(item => item.name),
          type: 'scroll',
          orient: 'vertical',
          right: 0,
          top: 0
        }
      }
    }
  },
  methods: {
    // Helper function to get the date of a specific week in the year
    getDateOfISOWeek: function (week, year) {
      // Start with January 1st of the specified year
      const jan1 = new Date(year, 0, 1)

      // Calculate days since January 1st (each week is exactly 7 days)
      // Week 1 starts on Jan 1, Week 2 starts on Jan 8, etc.
      const daysSinceJan1 = (week - 1) * 7

      // Create date for the first day of the requested week
      const result = new Date(jan1)
      result.setDate(jan1.getDate() + daysSinceJan1)

      return result
    },
    formatTooltip: function (params) {
      var tooltip = `<div style="margin-bottom: 5px;"><b style="font-size: 14px;">${this.granularityInternal === 'Weekly' ? `${this.$t('components.userData.DataSeriesChart.week')} ${params[0].name}` : params[0].name}</b></div>`
      for (let index = params.length - 1; index >= 0; index--) {
        const param = params[index]
        const color = (param.color && param.color.colorStops && param.color.colorStops.length > 1 && param.color.colorStops[1] && param.color.colorStops[1].color)
          ? param.color.colorStops[1].color
          : (param.color || '#999999')
        if (!this.singleSeries) {
          tooltip += `<b style="color: ${color}">●</b>`
          tooltip += `<span style="font-size: 16px;">${param.seriesName}:</span>`
        }
        tooltip += `<span style="color: darkgrey; font-size: 18px;">${this.formatValue(param.value)}</span><br>`
      }
      return tooltip
    },
    aggregateYearSeries: function (granularity) {
      let func
      if (granularity === 'Weekly') {
        func = this.aggregateValuesByWeek
      } else if (granularity === 'Monthly') {
        func = this.aggregateValuesByMonth
      } else if (granularity === 'Quarterly') {
        func = this.aggregateValuesByQuarter
      }
      if (this.singleSeries) {
        let data = []
        for (const year of Object.keys(this.data.dates).sort()) {
          data = data.concat(func(this.data.dates[year], this.data.sums[year]))
        }
        return [{
          name: this.displayTitle,
          data: data,
          type: 'line',
          itemStyle: {
            color: this.allColors && this.index ? this.allColors[this.index % this.allColors.length] : null
          }
        }]
      } else {
        return Object.keys(this.data.sums)
          .sort() // Sort years chronologically
          .map(year => ({
            name: year,
            data: func(this.data.dates[year], this.data.sums[year]),
            type: 'line',
            itemStyle: {
              color: this.colors && year in this.colors ? this.colors[year] : null
            }
          }))
      }
    },
    getMonth: function (date) {
      // Get the month index (0-11) from the date
      const monthIndex = date.getMonth()
      // Return the corresponding 3-letter abbreviation
      return this.months[monthIndex]
    },
    getWeekNumber: function (date) {
      // Clone the date to avoid modifying the original
      const target = new Date(date.valueOf())
      // ISO week calculation logic:
      // Set to nearest Thursday (ISO weeks start on Monday)
      const dayNumber = (date.getDay() + 6) % 7
      target.setDate(target.getDate() - dayNumber + 3)
      // Get first Thursday of the year
      const firstThursday = new Date(target.getFullYear(), 0, 1)
      if (firstThursday.getDay() !== 4) {
        firstThursday.setMonth(0, 1 + ((4 - firstThursday.getDay()) + 7) % 7)
      }
      // Calculate week number
      const weekNumber = 1 + Math.ceil((target - firstThursday) / (7 * 24 * 3600 * 1000))
      return Math.min(weekNumber, 52) // Cap at 52
    },
    aggregateValuesByWeek: function (arrDates, arrVals) {
      // Initialize array with 52 zeros (one for each week)
      const weeklyTotals = Array(52).fill(0)
      // Process each date-value pair
      for (let i = 0; i < arrDates.length; i++) {
        const dateStr = arrDates[i]
        const value = arrVals[i]
        const [year, month, day] = this.parseDate(dateStr)
        // Create Date object (note: JS months are 0-indexed)
        // Assuming '20' prefix for YY if less than 100
        const fullYear = year < 100 ? 2000 + year : year
        const date = new Date(fullYear, month - 1, day)
        // Get week number (1-52)
        const weekNumber = this.getWeekNumber(date)
        // Add value to the corresponding week's total (adjust to 0-indexed)
        weeklyTotals[weekNumber - 1] += value
      }
      return weeklyTotals
    },
    aggregateValuesByMonth: function (arrDates, arrVals) {
      // Initialize array with 12 zeros (one for each month)
      const monthlyTotals = Array(12).fill(0)
      // Process each date-value pair
      for (let i = 0; i < arrDates.length; i++) {
        const dateStr = arrDates[i]
        const value = arrVals[i]
        const [year, month, day] = this.parseDate(dateStr)
        // Create Date object (note: JS months are 0-indexed)
        // Assuming '20' prefix for YY if less than 100
        const fullYear = year < 100 ? 2000 + year : year
        const date = new Date(fullYear, month - 1, day)
        // Get month index (0-11)
        const monthIndex = date.getMonth()
        // Add value to the corresponding month's total
        monthlyTotals[monthIndex] += value
      }
      return monthlyTotals
    },
    aggregateValuesByQuarter: function (arrDates, arrVals) {
      // Initialize array with 4 zeros (one for each quarter)
      const quarterlyTotals = Array(4).fill(0)
      // Process each date-value pair
      for (let i = 0; i < arrDates.length; i++) {
        const dateStr = arrDates[i]
        const value = arrVals[i]
        const [year, month, day] = this.parseDate(dateStr)
        // Create Date object (note: JS months are 0-indexed)
        // Assuming '20' prefix for YY if less than 100
        const fullYear = year < 100 ? 2000 + year : year
        const date = new Date(fullYear, month - 1, day)
        // Get quarter index (0-3)
        const quarterIndex = Math.floor(date.getMonth() / 3)
        // Add value to the corresponding quarter's total
        quarterlyTotals[quarterIndex] += value
      }
      return quarterlyTotals
    },
    formatValue: function (value) {
      return numbro(value).format({
        average: true,
        lowPrecision: false,
        mantissa: 1,
        optionalMantissa: true
      })
    },
    parseDate: function (dateStr) {
      let month, day, year
      // Parse date string
      if (dateStr.includes('/')) {
        // MM/DD/YY format
        [month, day, year] = dateStr.split('/').map(num => parseInt(num, 10))
      } else if (dateStr.includes('-')) {
        // YYYY-MM-DD format
        [year, month, day] = dateStr.split('-').map(num => parseInt(num, 10))
      } else {
        throw new Error('Invalid date format. Please use MM/DD/YY or YYYY-MM-DD')
      }
      return [year, month, day]
    }
  },
  props: {
    data: {
      type: Object,
      required: true
    },
    title: {
      type: String,
      required: false,
      default: null
    },
    granularity: {
      type: String,
      required: false
    },
    colors: {
      type: Object,
      required: false
    },
    allColors: {
      type: Array,
      required: false
    },
    singleSeries: {
      type: Boolean,
      required: false
    },
    index: {
      type: Number,
      required: false
    }
  },
  watch: {
    'granularity': {
      immediate: true,
      handler () {
        this.granularityInternal = this.granularity
      }
    }
  },
  components: {
    'v-chart': ECharts
  }
}
</script>
